import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import LeadService from "../services/LeadService";
import { getStageAccent, PAGE_BG, PURPLE, PURPLE_DARK } from "../components/leadConstants";

function apiErrorMessage(err, fallback) {
  return err?.response?.data?.error?.message
    || err?.response?.data?.message
    || err?.message
    || fallback;
}

const STAGE_TYPES = [
  { value: "open", label: "Open" },
  { value: "won", label: "Won (closed)" },
  { value: "lost", label: "Lost (closed)" },
];

function stageTypeValue(stage) {
  if (stage?.is_won) return "won";
  if (stage?.is_lost) return "lost";
  return "open";
}

function payloadFromForm(name, type) {
  return {
    name: name.trim(),
    is_won: type === "won",
    is_lost: type === "lost",
  };
}

const PipelineStagesView = () => {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [stageToDelete, setStageToDelete] = useState(null);
  const [formName, setFormName] = useState("");
  const [formType, setFormType] = useState("open");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const loadStages = useCallback(async () => {
    setLoading(true);
    try {
      const data = await LeadService.getPipelineStages();
      setStages(
        [...data].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
      );
    } catch {
      setSnackbar({ open: true, message: "Failed to load pipeline stages", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStages();
  }, [loadStages]);

  const openCreateDialog = () => {
    setEditingStage(null);
    setFormName("");
    setFormType("open");
    setDialogOpen(true);
  };

  const openEditDialog = (stage) => {
    setEditingStage(stage);
    setFormName(stage.name || "");
    setFormType(stageTypeValue(stage));
    setDialogOpen(true);
  };

  const handleSaveStage = async () => {
    if (!formName.trim()) return;
    setSaving(true);
    try {
      const payload = payloadFromForm(formName, formType);
      if (editingStage) {
        await LeadService.updatePipelineStage(editingStage.id, payload);
        setSnackbar({ open: true, message: "Stage updated", severity: "success" });
      } else {
        await LeadService.createPipelineStage(payload);
        setSnackbar({ open: true, message: "Stage created", severity: "success" });
      }
      setDialogOpen(false);
      await loadStages();
    } catch (err) {
      setSnackbar({
        open: true,
        message: apiErrorMessage(err, "Failed to save stage"),
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStage = async () => {
    if (!stageToDelete) return;
    setSaving(true);
    try {
      await LeadService.deletePipelineStage(stageToDelete.id);
      setSnackbar({ open: true, message: "Stage deleted", severity: "success" });
      setDeleteDialogOpen(false);
      setStageToDelete(null);
      await loadStages();
    } catch (err) {
      setSnackbar({
        open: true,
        message: apiErrorMessage(err, "Failed to delete stage"),
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const moveStage = async (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= stages.length) return;

    const reordered = [...stages];
    const [item] = reordered.splice(index, 1);
    reordered.splice(targetIndex, 0, item);

    setStages(reordered);
    try {
      const updated = await LeadService.reorderPipelineStages(reordered.map((s) => s.id));
      setStages(
        [...updated].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
      );
    } catch {
      setSnackbar({ open: true, message: "Failed to reorder stages", severity: "error" });
      await loadStages();
    }
  };

  return (
    <Box sx={{ minHeight: "100%", bgcolor: PAGE_BG }}>
      <Paper
        elevation={0}
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          borderBottom: "1px solid #e4e7ef",
          borderRadius: 0,
          bgcolor: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700} color={PURPLE_DARK}>
              Pipeline stages
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {stages.length} stage{stages.length === 1 ? "" : "s"} in your leads pipeline.{" "}
              <Link component={RouterLink} to="/leads" underline="hover" color={PURPLE}>
                Back to board
              </Link>
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
            sx={{
              bgcolor: PURPLE,
              fontWeight: 600,
              "&:hover": { bgcolor: PURPLE_DARK },
            }}
          >
            Add stage
          </Button>
        </Box>
      </Paper>

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: PURPLE }} />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ border: "1px solid #e4e7ef", borderRadius: 2, overflow: "hidden" }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f6f4fd" }}>
                  {["Order", "Stage", "Type", "Move", ""].map((label) => (
                    <TableCell
                      key={label || "actions"}
                      align={label ? "left" : "right"}
                      sx={{ fontWeight: 700, color: PURPLE_DARK, fontSize: "0.8rem" }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {stages.map((stage, index) => {
                  const accent = getStageAccent(stage);
                  const type = stageTypeValue(stage);
                  return (
                    <TableRow key={stage.id} hover>
                      <TableCell sx={{ color: "text.secondary", width: 72 }}>
                        {index + 1}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: accent.accent,
                              flexShrink: 0,
                            }}
                          />
                          <Typography fontWeight={600} color={PURPLE_DARK}>
                            {stage.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={type}
                          size="small"
                          sx={{
                            textTransform: "capitalize",
                            height: 22,
                            bgcolor: accent.chip,
                            color: PURPLE_DARK,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ width: 100 }}>
                        <Tooltip title="Move up">
                          <span>
                            <IconButton
                              size="small"
                              disabled={index === 0}
                              onClick={() => moveStage(index, -1)}
                            >
                              <ArrowUpIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Move down">
                          <span>
                            <IconButton
                              size="small"
                              disabled={index === stages.length - 1}
                              onClick={() => moveStage(index, 1)}
                            >
                              <ArrowDownIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEditDialog(stage)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            disabled={stages.length <= 1}
                            onClick={() => {
                              setStageToDelete(stage);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {stages.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No pipeline stages yet.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
          Stages appear left-to-right on the leads board in this order. A stage can only be deleted
          when it has no leads. Mark one stage as Won and one as Lost to close leads automatically.
        </Alert>
      </Box>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: PURPLE_DARK }}>
          {editingStage ? "Edit stage" : "Add stage"}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            label="Stage name"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            fullWidth
            autoFocus
          />
          <FormControl>
            <InputLabel id="stage-type-label">Stage type</InputLabel>
            <RadioGroup
              aria-labelledby="stage-type-label"
              value={formType}
              onChange={(e) => setFormType(e.target.value)}
            >
              {STAGE_TYPES.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio size="small" sx={{ color: PURPLE, "&.Mui-checked": { color: PURPLE } }} />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveStage}
            disabled={saving || !formName.trim()}
            sx={{ bgcolor: PURPLE, "&:hover": { bgcolor: PURPLE_DARK } }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => !saving && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete stage?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete &quot;{stageToDelete?.name}&quot;? Move or delete any leads in this stage first.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteStage} disabled={saving}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PipelineStagesView;
